import { EditableProTable } from "@ant-design/pro-components";
import React, { useMemo, useRef , useState, useCallback, useEffect} from "react";
import enUS from "antd/locale/en_US";
import { Button, ConfigProvider, Popconfirm, Tooltip } from "antd";
import Title from "antd/es/typography/Title";
import { useToggle } from '@uidotdev/usehooks'
import editorTableClasses from "../editable-table.module.scss";
import { useMutation } from "@tanstack/react-query";
import { addWellSalePrice, updateWellSalePrice, deleteWellSalePrice } from "service/salePrices";
import { uploadSalePrices } from "service/economics/upload";
import SalePricesDataImportModal from "./SalePricesDataImportModal";
import classes from './sale-prices-data-import.module.scss'
import classNames from "classnames";


const WellSalePricesEditor = ({
  isLoading,
  records = [],
  wellId,
  onRefetch,
}) => {
  const container = useRef(null);

  const importFileInput = useRef(null);
  /**
   * @type {[File, React.Dispatch<File>]}
   */
  const [csvFile, setCSVFile] = useState(null);
  const [isImportModalOpen, toggleImportModal] = useToggle(false);
  const [isImporting, setImporting] = useState(false);

  const onTriggerImport = useCallback(() => {
    if (!importFileInput.current) return;
    importFileInput.current.value = null;
    importFileInput.current.click();
  }, []);

  const onCSVFileChange = useCallback(
    (e) => {
      const {
        target: { files },
      } = e;

      const file = files[0];
      setCSVFile(file ?? null);
      toggleImportModal(true);
    },
    [toggleImportModal]
  );

  const onCloseImportModal = useCallback(() => {
    setCSVFile(null);
    toggleImportModal(false);
  }, [toggleImportModal]);

  const onImportData = useCallback(
    async (data) => {
      try {
        setImporting(true);
        await uploadSalePrices(data);
        onRefetch?.();
        onCloseImportModal();
      } finally {
        setImporting(false);
      }
    },
    [onCloseImportModal, onRefetch]
  );

  const { mutate: deleteSalePrice } = useMutation({
    mutationFn: (id) => deleteWellSalePrice(wellId, [id]),
    onSuccess: () => onRefetch(),
  });

  const { mutate: updateSalePrice } = useMutation({
    mutationFn: (record) => updateWellSalePrice(wellId, [record]),
    onSuccess: () => onRefetch(),
  });

  const columns = useMemo(
    /**
     *
     * @returns {import('@ant-design/pro-components').ProColumns}
     */
    () => [
      {
        title: "Date",
        dataIndex: "date",
        editable: true,
      },
      {
        title: "Oil Sale Price ($/bbl)",
        dataIndex: "oil",
        editable: true,
      },
      {
        title: "NG Sale Price ($/MSCF)",
        dataIndex: "gas",
        editable: true,
      },
      {
        title: "NGL Sale Price ($/gln)",
        dataIndex: "ngl",
        editable: true,
      },
      {
        key: "actions",
        valueType: "option",
        title: "Actions",
        render: (_, record, index, action) => {
          return (
            <div className="d-flex gap-2">
              <Button onClick={() => action.startEditable?.(record.id)}>
                Edit
              </Button>
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => deleteSalePrice(record.id)}
              >
                <Button>Delete</Button>
              </Popconfirm>
            </div>
          );
        },
      },
    ],
    [deleteSalePrice]
  );

  return (
    <ConfigProvider
      locale={enUS}
      componentSize="middle"
      theme={{
        token: {},
        components: {},
      }}
    >
      <SalePricesDataImportModal
        open={isImportModalOpen}
        file={csvFile}
        isImporting={isImporting}
        onImport={onImportData}
        onClose={onCloseImportModal}
      />
      <div ref={container} className="vstack h-100">
        <div
          className={classNames(
            "mx-4 hstack justify-content-between",
            classes.tableHeader
          )}
        >
          <Title level={5} className="text-light">
            Sale Prices
          </Title>
          <div className="d-flex">
            <Tooltip
              placement="top"
              title={`CSV file format should be: Well Name, Asset Name, Or UWI, Date, Oil, Gas, NGL. The first row should be header`}
            >
              <Button onClick={onTriggerImport}>Import CSV</Button>
              <input
                ref={importFileInput}
                type="file"
                accept="text/csv"
                onChange={onCSVFileChange}
                hidden
              />
            </Tooltip>
          </div>
        </div>
        <EditableProTable
          loading={isLoading}
          alwaysShowAlert={false}
          tableAlertRender={false}
          tableAlertOptionRender={false}
          pagination={false}
          search={false}
          rowKey="id"
          sticky={true}
          columns={columns}
          value={records}
          className={editorTableClasses.editableTable}
          cardProps={{
            className: editorTableClasses.card,
          }}
          tableClassName={editorTableClasses.table}
          scroll={{
            y: container.current?.clientHeight - 60,
          }}
          recordCreatorProps={{
            style: {
              display: "none",
            },
          }}
          editable={{
            onSave: (id, record) => {
              if (record.isNewRecord) return;
              updateSalePrice(record);
            },
          }}
        />
      </div>
    </ConfigProvider>
  );
};

export default WellSalePricesEditor;
